import {
  createBrowserRouter,
  type LoaderFunctionArgs,
  Navigate,
  redirect,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

import { RegisterRedirect } from "./components/util/RegisterRedirect";
import { RemoveTrailingSlash } from "./components/util/RemoveTrailingSlash";
import { RootRedirect } from "./components/util/RootRedirect";
import { EntryLayout } from "./layout/EntryLayout";
import { MainLayout } from "./layout/MainLayout";
import { PublicLayout } from "./layout/PublicLayout";
import { CompleteRegistrationPage } from "./pages/auth/CompleteRegistrationPage";
import { CompleteYandexRegistrationPage } from "./pages/auth/CompleteYandexRegistrationPage";
import { EmailVerificationPage } from "./pages/auth/EmailVerificationPage";
import { ExistingGoogleEmailPage } from "./pages/auth/ExistingGoogleEmailPage";
import { ExistingYandexEmailPage } from "./pages/auth/ExistingYandexEmailPage";
import { LoginPage } from "./pages/auth/LoginPage";
import { PasswordPage } from "./pages/auth/PasswordPage";
import { PasswordRecoveryPage } from "./pages/auth/PasswordRecoveryPage";
import { PasswordResetDonePage } from "./pages/auth/PasswordResetDonePage";
import { PasswordResetSetupPage } from "./pages/auth/PasswordResetSetupPage";
import { PasswordResetSuccessPage } from "./pages/auth/PasswordResetSuccessPage";
import { SignupDetailsPage1 } from "./pages/auth/SignupDetailsPage1";
import { SignupDetailsPage2 } from "./pages/auth/SignupDetailsPage2";
import { SignupPage } from "./pages/auth/SignupPage";
import { YandexNoEmailPage } from "./pages/auth/YandexNoEmailPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { OnboardingUnsubscribePage } from "./pages/OnboardingUnsubscribePage";
import { OnPremisePage } from "./pages/OnPremisePage";
import { ProfilePage } from "./pages/ProfilePage";
import { PublicProfilePage } from "./pages/PublicProfilePage";
import { SoftwarePage } from "./pages/SoftwarePage";
import { SubscriptionCancelPage } from "./pages/SubscriptionCancelPage";
import { SubscriptionManagePage } from "./pages/SubscriptionManagePage";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import { SubscriptionUpdatePage } from "./pages/SubscriptionUpdatePage";
import { SupportPage } from "./pages/SupportPage";
import { TeamManagePage } from "./pages/TeamManagePage";
import { TestErrorsPage } from "./pages/TestErrorsPage";
import { TestIndexPage } from "./pages/TestIndexPage";
import { TestModalsPage } from "./pages/TestModalsPage";
import { TestOnboardingUnsubscribePage } from "./pages/TestOnboardingUnsubscribePage";
import { TestSubscriptionInfoPage } from "./pages/TestSubscriptionInfoPage";
import { TestSubscriptionManagePage } from "./pages/TestSubscriptionManagePage";
import { TestUnsubscribePage } from "./pages/TestUnsubscribePage";
import { TestWebGisPage } from "./pages/TestWebGisPage";
import { UnsubscribePage } from "./pages/UnsubscribePage";
import { WebGisCreatePage } from "./pages/WebGisCreatePage";
import { WebGisPage } from "./pages/WebGisPage";

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const redirectWithSearch = (pathname: string, requestUrl: string) =>
  redirect(`${pathname}${new URL(requestUrl).search}`);

export const router = sentryCreateBrowserRouter([
  {
    element: <RemoveTrailingSlash />,
    children: [
      { path: "/", element: <RootRedirect /> },
      { path: "/unsubscribe/:hash", element: <UnsubscribePage /> },
      { path: "/onboarding-unsubscribe/:hash", element: <OnboardingUnsubscribePage /> },
      { path: "/test/unsubscribe", element: <TestUnsubscribePage /> },
      { path: "/test/onboarding-unsubscribe", element: <TestOnboardingUnsubscribePage /> },
      {
        element: <MainLayout />,
        children: [
          {
            path: "/profile",
            element: <ProfilePage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/subscription",
            element: <SubscriptionPage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/subscription/update",
            loader: ({ request }: LoaderFunctionArgs) =>
              redirectWithSearch(
                new URL(request.url).searchParams.has("plan")
                  ? "/subscription/update/manage"
                  : "/subscription/update/plans",
                request.url,
              ),
          },
          {
            path: "/subscription/update/manage",
            element: <SubscriptionManagePage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/subscription/update/plans",
            element: <SubscriptionUpdatePage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/subscription/update/checkout",
            loader: ({ request }: LoaderFunctionArgs) =>
              redirectWithSearch("/subscription/update/manage", request.url),
          },
          {
            path: "/subscription/update/cancel",
            element: <SubscriptionCancelPage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/webgis",
            element: <WebGisPage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/webgis/create",
            element: <WebGisCreatePage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/software",
            element: <SoftwarePage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/teammanage",
            element: <Navigate to="/teammanage/owner" replace />,
          },
          {
            path: "/teammanage/owner",
            element: <TeamManagePage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/teammanage/member",
            element: <TeamManagePage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/myngidonpremises",
            element: <OnPremisePage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/support",
            element: <SupportPage />,
            handle: { footerVariant: "dark" },
          },
          {
            path: "/external-services",
            element: <Navigate to="/software?tab=api-keys" replace />,
          },
          {
            path: "/test",
            handle: { footerVariant: "dark" },
            children: [
              { index: true, element: <TestIndexPage /> },
              { path: "modal", element: <TestModalsPage /> },
              { path: "errors", element: <TestErrorsPage /> },
              {
                path: "subscriptioninfo",
                element: <TestSubscriptionInfoPage />,
              },
              {
                path: "subscriptionmanage",
                element: <TestSubscriptionManagePage />,
                handle: { devMocked: true },
              },
              {
                path: "webgis",
                element: <TestWebGisPage />,
                handle: { devMocked: true },
              },
            ],
          },
        ],
      },
      {
        element: <EntryLayout />,
        children: [
          {
            path: "/login",
            children: [
              {
                index: true,
                loader: ({ request }: LoaderFunctionArgs) =>
                  redirectWithSearch("/login/1", request.url),
              },
              { path: "1", element: <LoginPage /> },
              { path: "2", element: <PasswordPage /> },
            ],
          },
          {
            path: "/signup",
            children: [
              {
                index: true,
                loader: ({ request }: LoaderFunctionArgs) =>
                  redirectWithSearch("/signup/0", request.url),
              },
              { path: "0", element: <SignupPage /> },
              {
                path: "email-verification",
                element: <EmailVerificationPage />,
              },
              { path: "details/1", element: <SignupDetailsPage1 /> },
              { path: "details/2", element: <SignupDetailsPage2 /> },
            ],
          },
          {
            path: "/reg",
            element: <RegisterRedirect />,
          },
          {
            path: "/registration/complete",
            element: <CompleteRegistrationPage />,
          },
          {
            path: "/registration/complete/yandex",
            element: <CompleteYandexRegistrationPage />,
          },
          {
            path: "/registration/complete/yandex/no_email",
            element: <YandexNoEmailPage />,
          },
          {
            path: "/registration/existing-google-email",
            element: <ExistingGoogleEmailPage />,
          },
          {
            path: "/registration/existing-yandex-email",
            element: <ExistingYandexEmailPage />,
          },
          {
            path: "/password/reset",
            children: [
              { index: true, element: <PasswordRecoveryPage /> },
              { path: "done", element: <PasswordResetDonePage /> },
              { path: "setup", element: <PasswordResetSetupPage /> },
              { path: ":token", element: <PasswordResetSetupPage /> },
              { path: "success", element: <PasswordResetSuccessPage /> },
            ],
          },
        ],
      },
      {
        element: <PublicLayout />,
        children: [
          {
            path: "/public_profile/:guid",
            element: <PublicProfilePage />,
          },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
